import { type ProcessStepRecordingDisplay, type CapacityBinding } from '@bliks-io/contracts';
import { resolveRefName, formatDate, type AssigneeLookup } from '@bliks-io/ui';
import { meanSurveyMetric, firstSurveyFrequency } from '@/lib/surveyMetrics';
import {
  buildHelperSystemEntries,
  buildHelperPersonEntries,
  buildHelperOrgFunctionEntries,
  buildHelperDocumentEntries,
  type HelperEntry
} from './ProcessStepHelperColumns';

/**
 * The `Schritte` (steps) sheet of the Process Detail Excel export — the node
 * half of the nodes-and-edges workbook (see `processExcelWorkbook.ts`). One row
 * per step, mirroring most columns of `ProcessStepsTable` (its per-rule
 * output/conditions move to the `Verzweigungen` sheet). The data is already
 * loaded and populated by `useProcess`, so the sheet is built in the browser
 * from the same `ProcessStepRecordingDisplay[]` the table renders, resolving the
 * unpopulated `{ type, id }` refs (assignee, who-else, helper systems, helper
 * functions) against the shared `AssigneeLookup` so exported names match the screen.
 *
 * Every step contributes exactly one row — step-level metadata appears once and
 * is never duplicated, so the sheet filters, sorts and sums cleanly. A step's
 * per-rule conditions/outputs and its successor edges live on the separate
 * `Verzweigungen` sheet (`processEdges.ts`).
 */

/** Localized header labels for the steps sheet, plus the two action-type words. */
export interface ProcessStepsSheetLabels {
  stepNumber: string;
  type: string;
  step: string;
  stepDescription: string;
  inputs: string;
  assignedTo: string;
  recordedBy: string;
  whoElse: string;
  helperSystems: string;
  helperPersons: string;
  helperOrgFunctions: string;
  helperDocuments: string;
  lastChange: string;
  rating: string;
  frequency: string;
  duration: string;
  capacity: string;
  capacitySource: string;
  /** Cell value for a step whose effective capacity comes from the surveys. */
  capacitySourceSurvey: string;
  /** Cell value for a step whose effective capacity is the manual entry. */
  capacitySourceManual: string;
  /** Cell value for a task step's type column. */
  taskType: string;
  /** Cell value for a decision step's type column. */
  decisionType: string;
}

export interface BuildStepsOptions {
  lookup: AssigneeLookup;
  stepCapacities?: Record<string, CapacityBinding>;
  labels: ProcessStepsSheetLabels;
  /** Resolves a survey frequency enum value (+ optional count) to its localized label. */
  formatFrequency: (value: string, timesPerPeriod?: number | null) => string;
  /** Append the survey/capacity metric columns (org-manager opt-in). */
  includeMetrics: boolean;
}

export type CellValue = string | number;

interface StepColumn {
  header: string;
  /** Column width in characters (SheetJS `wch`). */
  width: number;
  value: (step: ProcessStepRecordingDisplay) => CellValue;
}

/**
 * The step's display name, mirroring `StepCard`/`ProcessStepsTable`: the
 * AI-generated name, falling back to the original task description. Shared with
 * the edges sheet, where it is the cached value behind the VLOOKUP that pulls
 * the same name from this sheet.
 */
export function formatStepName(step: ProcessStepRecordingDisplay): string {
  return step.generatedName || step.task?.taskDescription || '';
}

/**
 * 1-based column index of the step-name (`Prozessschritt`) column in the steps
 * sheet — the 3rd column (after `Schritt Nr.` and `Typ`). The edges sheet's
 * VLOOKUP reads this column to pull step/successor names, so this is the single
 * source of truth for that offset; a regression test pins it to the built
 * header (see processStepsExcel.test.ts) so reordering the columns can't break
 * the lookup silently.
 */
export const STEPS_NAME_COLUMN_INDEX = 3;

/** Sort comparator by `stepNumber`, with unnumbered steps last. Shared with the edges sheet. */
export function compareByStepNumber(
  a: { stepNumber?: number },
  b: { stepNumber?: number }
): number {
  return (a.stepNumber ?? Number.MAX_SAFE_INTEGER) - (b.stepNumber ?? Number.MAX_SAFE_INTEGER);
}

function joinNames(values: ReadonlyArray<string | undefined | null>): string {
  return values.filter((v): v is string => !!v).join(', ');
}

function joinHelpers(entries: HelperEntry[]): string {
  return joinNames(entries.map((entry) => entry.name));
}

// The step's description, mirroring `StepCard`: the AI-generated step
// description, falling back to the original task description for task steps.
function formatDescription(step: ProcessStepRecordingDisplay): string {
  return (
    step.generatedDescription ||
    (step.actionType === 'task' ? (step.task?.taskDescription ?? '') : '')
  );
}

function formatInputs(step: ProcessStepRecordingDisplay): string {
  // `inputs` is a discriminated union of objects (never a string); each input's
  // name falls back to its description.
  return joinNames((step.inputs ?? []).map((input) => input.name || input.description));
}

function recordedByName(step: ProcessStepRecordingDisplay): string {
  const recordedBy = step.recordedBy;
  return recordedBy ? `${recordedBy.firstName} ${recordedBy.lastName}`.trim() : '';
}

// Mirrors `ProcessStepMembersCell`: only accepted/pending User entries surface,
// resolved against the lookup; unresolvable refs are dropped.
function whoElseNames(step: ProcessStepRecordingDisplay, lookup: AssigneeLookup): string {
  return joinNames(
    (step.whoElseDoesThis ?? [])
      .filter(
        (item) => (item.status === 'accepted' || item.status === 'pending') && item.model === 'User'
      )
      .map((item) => resolveRefName(item.entity, 'User', lookup))
  );
}

// Average a numeric survey metric across a step's surveys, rounded to 2 dp;
// '' when no survey carries the value.
function averageSurveyMetric(
  step: ProcessStepRecordingDisplay,
  select: (
    survey: NonNullable<ProcessStepRecordingDisplay['surveys']>[number]
  ) => number | undefined
): CellValue {
  const mean = meanSurveyMetric(step.surveys, select);
  return mean === null ? '' : Math.round(mean * 100) / 100;
}

// The manual primitives of a step whose effective capacity source is the manual
// entry — the metrics block reports the source that was actually calculated.
function effectiveManualCapacity(
  step: ProcessStepRecordingDisplay,
  stepCapacities: Record<string, CapacityBinding> | undefined
): ProcessStepRecordingDisplay['manualCapacity'] {
  return stepCapacities?.[step._id]?.capacitySource === 'manual' ? step.manualCapacity : undefined;
}

/**
 * Build the steps worksheet as an array-of-arrays (header row + one row per
 * step, sorted by step number like the table) together with the matching column
 * widths. Pure — no SheetJS — so it is unit-testable on its own.
 */
export function buildProcessStepsSheetRows(
  steps: ProcessStepRecordingDisplay[],
  options: BuildStepsOptions
): { rows: CellValue[][]; colWidths: { wch: number }[] } {
  const { lookup, stepCapacities, labels, formatFrequency, includeMetrics } = options;

  const sortedSteps = [...steps].sort(compareByStepNumber);

  const columns: StepColumn[] = [
    { header: labels.stepNumber, width: 8, value: (step) => step.stepNumber ?? '' },
    {
      header: labels.type,
      width: 12,
      value: (step) => (step.actionType === 'decision' ? labels.decisionType : labels.taskType)
    },
    { header: labels.step, width: 36, value: (step) => formatStepName(step) },
    { header: labels.stepDescription, width: 48, value: (step) => formatDescription(step) },
    { header: labels.inputs, width: 40, value: (step) => formatInputs(step) },
    {
      header: labels.assignedTo,
      width: 24,
      value: (step) => resolveRefName(step.assignedTo, step.assignedToModel, lookup) ?? ''
    },
    { header: labels.recordedBy, width: 24, value: (step) => recordedByName(step) },
    { header: labels.whoElse, width: 28, value: (step) => whoElseNames(step, lookup) },
    {
      header: labels.helperSystems,
      width: 28,
      value: (step) => joinHelpers(buildHelperSystemEntries(step, lookup))
    },
    {
      header: labels.helperPersons,
      width: 28,
      value: (step) => joinHelpers(buildHelperPersonEntries(step, lookup))
    },
    {
      header: labels.helperOrgFunctions,
      width: 28,
      value: (step) => joinHelpers(buildHelperOrgFunctionEntries(step, lookup))
    },
    {
      header: labels.helperDocuments,
      width: 28,
      value: (step) => joinHelpers(buildHelperDocumentEntries(step))
    },
    { header: labels.lastChange, width: 16, value: (step) => formatDate(step.updatedAt) }
  ];

  if (includeMetrics) {
    columns.push(
      {
        header: labels.rating,
        width: 10,
        value: (step) => averageSurveyMetric(step, (s) => s.rating?.stars)
      },
      {
        header: labels.frequency,
        width: 16,
        value: (step) => {
          const manual = effectiveManualCapacity(step, stepCapacities);
          if (manual) {
            return formatFrequency(manual.frequency, manual.frequencyTimesPerPeriod);
          }
          const frequency = firstSurveyFrequency(step.surveys);
          return frequency?.value ? formatFrequency(frequency.value, frequency.timesPerPeriod) : '';
        }
      },
      {
        header: labels.duration,
        width: 14,
        value: (step) =>
          effectiveManualCapacity(step, stepCapacities)?.durationMinutes ??
          averageSurveyMetric(step, (s) => s.duration?.value)
      },
      {
        header: labels.capacity,
        width: 16,
        value: (step) => stepCapacities?.[step._id]?.hoursPerYear ?? ''
      },
      {
        header: labels.capacitySource,
        width: 18,
        value: (step) => {
          const source = stepCapacities?.[step._id]?.capacitySource;
          if (source === 'manual') return labels.capacitySourceManual;
          if (source === 'survey') return labels.capacitySourceSurvey;
          return '';
        }
      }
    );
  }

  const rows: CellValue[][] = [
    columns.map((column) => column.header),
    ...sortedSteps.map((step) => columns.map((column) => column.value(step)))
  ];
  const colWidths = columns.map((column) => ({ wch: column.width }));

  return { rows, colWidths };
}
